import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useCharacterCreationStore } from '../stores/characterCreationStore';
import { usePlaySessionStore, type SpellSlotState } from '../stores/playSessionStore';
import { useCharacterBenefits } from '../hooks/useCharacterBenefits';
import { PlayModeNav, type PlayModeTab } from '../components/play-mode/PlayModeNav';
import { PlayModeCombat } from '../components/play-mode/PlayModeCombat';
import { PlayModeRolePlay } from '../components/play-mode/PlayModeRolePlay';
import { PlayModeSheet } from '../components/play-mode/PlayModeSheet';
import { NumericInputModal } from '../components/ui/NumericInputModal';
import { CombatLayoutToggle } from '../components/play-mode/CombatLayoutToggle';
import { useCombatLayout } from '../hooks/useCombatLayout';
import { useDiceRollerStore } from '../stores/diceRollerStore';
import type { RollMode } from '../stores/diceRollerStore';

type HPModalType = 'damage' | 'heal' | 'temp' | null;

export function PlayModePage() {
  const { rulesetId } = useParams<{ rulesetId: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<PlayModeTab>('sheet');
  const [hpModalType, setHpModalType] = useState<HPModalType>(null);

  const { draft } = useCharacterCreationStore();
  const benefits = useCharacterBenefits();
  const { rollMode, setRollMode } = useDiceRollerStore();

  const {
    activeSession,
    initializeSession,
    takeDamage,
    heal,
    setTempHp,
    toggleCondition,
    useSpellSlot,
    restoreSpellSlot,
    setConcentration,
    setSneakAttackEnabled,
    shortRest,
    longRest,
    addDeathSaveSuccess,
    addDeathSaveFailure,
    resetDeathSaves,
    useHitDie,
    useResource,
    restoreResource,
    setSessionNotes,
    setLayoutPreference,
    toggleBardicInspiration,
    toggleDmInspiration,
    addCustomEffect,
    removeCustomEffect,
  } = usePlaySessionStore();

  // Get character ID for session (UUIDs provide stable identification across renames)
  const characterId = draft.id;

  // Combine all spell IDs for layout detection
  const allSpellIds = [
    ...draft.selectedCantrips,
    ...draft.knownSpells,
    ...draft.preparedSpells,
  ];

  // Get layout preference and auto-detected layout for combat toggle
  const layoutPreference = activeSession?.layoutPreference ?? 'auto';
  const { autoDetectedLayout } = useCombatLayout(
    draft.attacks.length,
    allSpellIds,
    activeSession?.spellSlots,
    layoutPreference
  );

  // Initialize or load session when component mounts
  useEffect(() => {
    // Skip if no character ID (shouldn't happen but be safe)
    if (!characterId) return;

    if (!activeSession || activeSession.characterId !== characterId) {
      // Build spell slots from character spellcasting data
      const spellSlots: Record<string, SpellSlotState> = {};
      if (benefits.spellcasting?.spellSlots) {
        for (const [level, max] of Object.entries(benefits.spellcasting.spellSlots)) {
          if (max > 0) {
            spellSlots[level] = { max, used: 0 };
          }
        }
      }

      // Build hit dice from character
      const hitDice: Record<string, { max: number; used: number }> = {};
      benefits.hitDice.forEach(({ die, count }) => {
        if (hitDice[die]) {
          hitDice[die].max += count;
        } else {
          hitDice[die] = { max: count, used: 0 };
        }
      });

      // Build class resources (e.g., Sorcery Points, Ki Points)
      const classResources: Record<string, { max: number; used: number }> = {};
      // TODO: Calculate class-specific resources

      initializeSession(characterId, benefits.hitPoints, spellSlots, hitDice, classResources);
    }
  }, [characterId, activeSession, benefits.hitPoints, benefits.hitDice, benefits.spellcasting, initializeSession]);

  // Compute Bard level for bardic inspiration die
  const bardLevel = benefits.classLevels.find((cl) => cl.classId === 'bard')?.level ?? 0;

  // Handle navigation back
  const handleBack = useCallback(() => {
    if (rulesetId) {
      navigate(`/${rulesetId}/create`, { state: { fromLibrary: true } });
    } else {
      navigate('/');
    }
  }, [navigate, rulesetId]);

  // Handle damage/heal modal openers
  const handleDamage = useCallback(() => setHpModalType('damage'), []);
  const handleHeal = useCallback(() => setHpModalType('heal'), []);
  const handleAddTempHp = useCallback(() => setHpModalType('temp'), []);

  // Handle modal submission
  const handleHpModalSubmit = useCallback(
    (value: number) => {
      switch (hpModalType) {
        case 'damage':
          takeDamage(value);
          break;
        case 'heal':
          heal(value);
          break;
        case 'temp':
          setTempHp(value);
          break;
      }
    },
    [hpModalType, takeDamage, heal, setTempHp]
  );

  // If no character, redirect
  if (!draft.name) {
    return (
      <div className="min-h-screen bg-background-primary flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-display text-2xl text-text-primary mb-4">NO CHARACTER FOUND</h1>
          <p className="text-text-secondary mb-4">Create a character first to use Play Mode.</p>
          <button
            onClick={() => navigate(`/${rulesetId}/create`, { state: { freshStart: true } })}
            className="px-6 py-2 bg-accent-gold text-background-primary font-display rounded"
          >
            CREATE CHARACTER
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-primary pb-16">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-background-secondary border-b border-accent-bronze/30">
        <div className="flex items-center justify-between px-4 py-3">
          <button
            onClick={handleBack}
            className="text-text-secondary hover:text-accent-gold transition-colors"
          >
            ← Back
          </button>

          <div className="text-center flex-1">
            <h1 className="font-display text-lg text-accent-gold tracking-wider">
              {draft.name}
            </h1>
            <p className="text-xs text-text-muted">
              Level {benefits.characterLevel}{' '}
              {benefits.classLevels.map((c) => c.className).join(' / ')}
            </p>
          </div>

          <div className="flex gap-2 items-center">
            {activeTab === 'combat' && (
              <CombatLayoutToggle
                preference={layoutPreference}
                autoDetectedLayout={autoDetectedLayout}
                onPreferenceChange={setLayoutPreference}
              />
            )}
            <button
              onClick={shortRest}
              className="px-3 py-1.5 text-xs bg-background-elevated hover:bg-accent-bronze/20
                text-text-secondary hover:text-text-primary rounded transition-colors font-display"
            >
              SHORT REST
            </button>
            <button
              onClick={longRest}
              className="px-3 py-1.5 text-xs bg-accent-gold/20 hover:bg-accent-gold/30
                text-accent-gold rounded transition-colors font-display"
            >
              LONG REST
            </button>
          </div>
        </div>

        {/* Quick Stats Row */}
        <div className="flex justify-center gap-4 px-4 pb-2 text-xs">
          <div className="px-2 py-1 bg-background-elevated rounded">
            <span className="text-text-muted">AC </span>
            <span className="font-mono font-bold text-text-primary">
              {10 + benefits.modifiers.dexterity}
            </span>
          </div>
          <div className="px-2 py-1 bg-background-elevated rounded">
            <span className="text-text-muted">Speed </span>
            <span className="font-mono font-bold text-text-primary">{benefits.speed} ft</span>
          </div>
          <div className="px-2 py-1 bg-background-elevated rounded">
            <span className="text-text-muted">Init </span>
            <span className="font-mono font-bold text-text-primary">
              {benefits.modifiers.dexterity >= 0 ? '+' : ''}
              {benefits.modifiers.dexterity}
            </span>
          </div>
          <div className="px-2 py-1 bg-background-elevated rounded">
            <span className="text-text-muted">Prof </span>
            <span className="font-mono font-bold text-accent-gold">+{benefits.proficiencyBonus}</span>
          </div>
        </div>
      </header>

      {/* Advantage/Disadvantage toggle — synced with dice roller panel */}
      <div className="sticky top-[88px] z-30 bg-background-primary border-b border-background-elevated">
        <div className="flex justify-center gap-2 p-2">
          {([
            { mode: 'normal' as RollMode, label: 'Normal' },
            { mode: 'advantage' as RollMode, label: '▲ Adv' },
            { mode: 'disadvantage' as RollMode, label: '▼ Dis' },
          ]).map(({ mode, label }) => (
            <button
              key={mode}
              onClick={() => setRollMode(mode)}
              className={`px-3 py-1 text-xs rounded font-display uppercase tracking-wide transition-colors ${
                rollMode === mode
                  ? mode === 'advantage'
                    ? 'bg-success/20 text-success'
                    : mode === 'disadvantage'
                      ? 'bg-error/20 text-error'
                      : 'bg-accent-gold/20 text-accent-gold'
                  : 'bg-background-elevated text-text-muted hover:text-text-secondary'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <main className="p-4">
        {activeTab === 'combat' && (
          <PlayModeCombat
            benefits={benefits}
            session={activeSession}
            attacks={draft.attacks}
            spells={{
              cantrips: draft.selectedCantrips,
              knownSpells: draft.knownSpells,
              preparedSpells: draft.preparedSpells,
            }}
            onDamage={handleDamage}
            onHeal={handleHeal}
            onAddTempHp={handleAddTempHp}
            onToggleCondition={toggleCondition}
            onDeathSaveSuccess={addDeathSaveSuccess}
            onDeathSaveFailure={addDeathSaveFailure}
            onResetDeathSaves={resetDeathSaves}
            onUseHitDie={useHitDie}
            onUseResource={useResource}
            onRestoreResource={restoreResource}
            onUseSpellSlot={useSpellSlot}
            onRestoreSpellSlot={restoreSpellSlot}
            onSetConcentration={setConcentration}
            onSetSneakAttackEnabled={setSneakAttackEnabled}
            bardicInspiration={activeSession?.bardicInspiration ?? false}
            dmInspiration={activeSession?.dmInspiration ?? false}
            bardLevel={bardLevel}
            customEffects={activeSession?.customEffects ?? []}
            onToggleBardicInspiration={toggleBardicInspiration}
            onToggleDmInspiration={toggleDmInspiration}
            onAddCustomEffect={addCustomEffect}
            onRemoveCustomEffect={removeCustomEffect}
          />
        )}
        {activeTab === 'roleplay' && (
          <PlayModeRolePlay
            benefits={benefits}
            draft={draft}
            sessionNotes={activeSession?.sessionNotes || ''}
            onNotesChange={setSessionNotes}
          />
        )}
        {activeTab === 'sheet' && (
          <PlayModeSheet
            benefits={benefits}
            session={activeSession}
            equipment={{
              customEquipment: draft.customEquipment,
              currency: draft.currency,
              attacks: draft.attacks,
            }}
            draft={draft}
            spells={{
              cantrips: draft.selectedCantrips,
              knownSpells: draft.knownSpells,
              preparedSpells: draft.preparedSpells,
            }}
            onDamage={handleDamage}
            onHeal={handleHeal}
            onToggleCondition={toggleCondition}
            onDeathSaveSuccess={addDeathSaveSuccess}
            onDeathSaveFailure={addDeathSaveFailure}
            onUseHitDie={useHitDie}
            onUseSpellSlot={useSpellSlot}
            onRestoreSpellSlot={restoreSpellSlot}
            onUseResource={useResource}
            onRestoreResource={restoreResource}
            onSetConcentration={setConcentration}
            onToggleBardicInspiration={toggleBardicInspiration}
            onToggleDmInspiration={toggleDmInspiration}
            onRemoveCustomEffect={removeCustomEffect}
          />
        )}
      </main>

      {/* Bottom Navigation */}
      <PlayModeNav
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />

      {/* HP Modification Modal */}
      <NumericInputModal
        isOpen={hpModalType !== null}
        onClose={() => setHpModalType(null)}
        onSubmit={handleHpModalSubmit}
        title={
          hpModalType === 'damage'
            ? 'Take Damage'
            : hpModalType === 'heal'
              ? 'Heal'
              : 'Add Temp HP'
        }
        placeholder={
          hpModalType === 'damage'
            ? 'Damage taken'
            : hpModalType === 'heal'
              ? 'HP restored'
              : 'Temp HP'
        }
        submitLabel={
          hpModalType === 'damage'
            ? 'Take Damage'
            : hpModalType === 'heal'
              ? 'Heal'
              : 'Add'
        }
        variant={hpModalType ?? 'default'}
      />
    </div>
  );
}
